import { FieldErrors, FieldValues } from 'react-hook-form';
import { typeSafeObjectKeys } from '@dist-marketplace-frontend/utils';
import { pushToDataLayer } from '../../../../../services/gtmService';
import {
  calculateTimeSpentInMilliseconds,
  removeStartTime,
} from '../../../../../helpers/browserStorageTimer';
import { FINANCE_APPLICATION_START_TIME } from '../../../constants';
import type {
  Provider,
  FinanceValidationError,
} from '../../../queries/schema/carFinance.schema';

import { type ApplicationTrackingArgs, RESULT } from './tracking.typed';

type ValidationErrorEntry = { field: string; message: string };

const findMessagesInErrors = (errors: FieldErrors): ValidationErrorEntry[] => {
  const entries: ValidationErrorEntry[] = [];
  const walk = (node: FieldErrors | FieldValues, path: string[] = []) => {
    if (!node) return;
    if (node.message || node.message === '') {
      const field = path.join('.');
      const message = node.message || node.type || 'validation';
      entries.push({ field, message });
    } else {
      typeSafeObjectKeys(node).forEach((key) => {
        walk(node[key], [...path, key]);
      });
    }
  };
  walk(errors);
  return entries;
};

// Fired only when the user successfully progresses past the given step,
// so each event reflects actual completion of that step.
const STEP_PROGRESSION_EVENTS: Record<number, string> = {
  2: 'next_personal_details_button',
  3: 'next_residential_details_button',
  4: 'next_employment_details_button',
  5: 'next_banking_info_button',
};

export const onStepProgressionEvent = (step: number) => {
  const event = STEP_PROGRESSION_EVENTS[step];
  if (event) pushToDataLayer({ event });
};

export const onConsentSubmitEvent = () => {
  pushToDataLayer({ event: 'consent_submit_application_button' });
};

const pushValidationError = (
  field: string,
  message: string,
  step: number,
  provider: Provider,
) => {
  pushToDataLayer({
    event: 'finance_application_validation_error',
    finance_validation_error_field: field,
    finance_validation_error_type: message,
    finance_validation_error_step: step,
    finance_provider: provider,
  });
};

export const onClientValidationErrorEvent = (
  errors: FieldErrors,
  step: number,
  provider: Provider,
) => {
  findMessagesInErrors(errors).forEach(({ field, message }) =>
    pushValidationError(field, message, step, provider),
  );
};

export const onServerValidationErrorEvent = (
  validationErrors: FinanceValidationError,
  step: number,
  provider: Provider,
) => {
  validationErrors.forEach(({ field, message }) =>
    pushValidationError(field, message, step, provider),
  );
};

export const onSubmitTrackingEvent = (args: ApplicationTrackingArgs) => {
  const { result, provider, errorFields = null } = args;
  const time =
    calculateTimeSpentInMilliseconds(FINANCE_APPLICATION_START_TIME) / 1000;

  if (result === RESULT.SUCCESS) {
    pushToDataLayer({
      event: 'finance_application_submitted',
      time,
      finance_provider: provider,
    });
    removeStartTime(FINANCE_APPLICATION_START_TIME);
  } else {
    pushToDataLayer({
      event: 'finance_application_error',
      error_fields: errorFields,
      time,
      finance_provider: provider,
    });
  }
};
